<template>
    <div>
        <section class="tabs-section">
            <NavBtns @navigatetotab="navigateToTab" :combo-ids="comboIds" :tabs="tabs" />
            <FlowNavigation currentStep="MyTrip" />
        </section>

        <section class="inner-content-section py-9">
        <div class="trip-container container">
            <!-- Page Header -->
            <div class="trip-header flex items-center mb-4">
                <div class="header-icon w-12 h-12 md:w-16 md:h-16 min-h-12 min-w-12 md:min-h-16 md:min-w-16 mr-3 bg-[var(--light-gray)] p-2 rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <path d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4H16.83L15 2H9Z" fill="currentColor" opacity="0.2"/>
                        <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="header-content">
                    <h1 class="header-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold">My Trip</h1>
                    <p class="header-subtitle text-base lg:text-lg text-[var(--tone-gray)]">Review and manage your selected packages</p>
                </div>
            </div>

            <!-- Combo package minimum selection warning -->
            <div v-if="showComboWarning" class="flex items-start gap-3 bg-[var(--light-primary,#fff8ee)] border border-[var(--primary)] rounded-lg px-4 py-3 text-sm text-[var(--charcoal-dark)] mb-4">
                <svg class="shrink-0 mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>
                    This booking requires at least <strong>2 packages</strong>. Please go back and select at least one more package before proceeding to checkout.
                </span>
            </div>

            <!-- Trip Timeline -->
            <div class="trip-timeline">
                <div v-for="(value, date, index) in bookings" :key="date" class="date-section mb-3">
                    <!-- Date Header -->
                    <div class="date-header flex items-center justify-between p-4 bg-white rounded-lg border border-[var(--bone)] cursor-pointer" @click="toggleDate(date)">
                        <div class="date-badge text-lg text-[var(--charcoal-dark)] font-semibold flex items-center">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
                                <path d="M3 10H21" stroke="currentColor" stroke-width="2"/>
                                <path d="M8 3V7M16 3V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <span class="ml-2">{{ formatDate(date) }}</span>
                        </div>
                        <svg 
                            class="toggle-icon" 
                            :class="{ 'rotated': expandedDates.includes(date) }"
                            width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>

                    <!-- Packages Timeline -->
                    <transition name="expand">
                        <div v-show="expandedDates.includes(date)" class="packages-timeline bg-white rounded-lg -mt-[1px] border border-[var(--bone)] p-4 relative before:content-[''] before:block before:w-[2px] before:absolute max-[767px]:before:left-5 before:left-[110px] before:top-4 before:bottom-4 before:bg-[var(--bone)]">
                            <div v-for="(item, idx) in value" :key="idx" class="package-card mb-5"
                                :class="{ 'is-booking': item.booking }">
                                
                                <!-- Time Indicator (if has slots) -->
                                <div v-if="item.data.package_has_slots" class="time-indicator bg-[var(--primary)] border-2 border-[var(--bone)] rounded-full flex items-center justify-center max-[767px]:w-28 max-[767px]:h-11 max-[767px]:mb-4 max-[767px]:ml-10 w-20 h-20 text-lg text-white font-semibold leading-tight text-center p-2 min-[768px]:-mb-20">
                                    <div class="time-dot"></div>
                                    <div class="time-label">{{ item.slot_time }}</div>
                                </div>
                                
                                <!-- Package Content -->
                                <div v-if="item.booking" class="package-content bg-white rounded-lg border border-[var(--bone)] p-4 relative max-[767px]:w-[calc(100%-50px)] w-[calc(100%-150px)] max-[767px]:ml-[40px] ml-[140px] box-border text-left shadow-[0_0_5px_2px_#eee]">
                                    <span class="absolute border-l border-t border-[var(--bone)] transform rotate-[-45deg] left-[-12px] top-[33px] w-[22px] h-[22px] bg-white"></span>
                                    <div class="package-main md:flex items-center">
                                        <div class="package-image max-[767px]:mb-2 min-w-52 min-h-44 w-52 h-44 bg-[var(--light-gray)] rounded-2xl mr-4 border border-[var(--bone)] overflow-hidden">
                                            <img v-if="item.data.package_image" :src="item.data.package_image" :alt="item.data.package_name" class="w-full h-full object-cover object-center" />
                                            <div v-else class="tour-image-placeholder w-full h-full flex items-center justify-center flex-col p-3">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                                </svg>
                                                <p class="placeholder-text text-[15px] text-[#9CA3AF] uppercase mt-2">Image coming soon</p>
                                            </div>
                                        </div>
                                        
                                        <div class="package-details">
                                            <h3 class="package-name text-lg text-[var(--charcoal-dark)] font-semibold">{{ item.data.package_name }}</h3>
                                            
                                            <div class="package-meta">
                                                <div class="meta-item text-base text-[var(--charcoal-dark)] flex items-center mb-1">
                                                    <svg class="min-w-4" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                        <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
                                                        <path d="M3 10H21" stroke="currentColor" stroke-width="2"/>
                                                    </svg>
                                                    <span class="ml-1">{{ formatDate(item.data.date) }}
                                                        <span v-if="item.data.package_has_slots">@ {{ item.data.time_date }}</span>
                                                    </span>
                                                </div>
                                                
                                                <div v-if="item.data.package_has_slots && item.data.duration" class="meta-item flex items-center mb-1">
                                                    <svg class="min-w-4" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none"/>
                                                        <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                                    </svg>
                                                    <span class="ml-1">{{ item.data.duration }}</span>
                                                </div>
                                            </div>

                                            <div class="package-guests">
                                                <div v-for="(pax, key) in item.data.people_group" :key="key" class="guest-item">
                                                    <span v-if="pax > 0" class="flex items-center mb-2 text-base text-[var(--charcoal-dark)]">
                                                        <svg class="min-w-4 mr-1" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="8" r="3" stroke="currentColor" stroke-width="2"/>
                                                            <path d="M6 21V19C6 16.7909 7.79086 15 10 15H14C16.2091 15 18 16.7909 18 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                                        </svg>
                                                        {{ pax }} {{ item.data.rate_group[key] }}
                                                    </span>
                                                </div>
                                            </div>

                                            <p class="package-description text-[15px] text-[var(--tone-gray)]">{{ item.data.short_description }}</p>

                                            <button @click="editPackage(item.data)" class="update-btn mt-2 btn-primary !flex items-center">
                                                <svg class="mr-2" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4C2.89543 4 2 4.89543 2 6V20C2 21.1046 2.89543 22 4 22H18C19.1046 22 20 21.1046 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                                    <path d="M18.5 2.5C19.3284 1.67157 20.6716 1.67157 21.5 2.5C22.3284 3.32843 22.3284 4.67157 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Continue Button -->
            <div class="continue-section mt-3">
                <button class="continue-button btn-primary" @click="continueToMaps">
                    Continue to Maps →
                </button>
            </div>
        </div>
        </section>
    </div>
</template>

<script>
import NavBtns from './Nav/NavBtns.vue';
import FlowNavigation from './Nav/FlowNavigation.vue';
import { getMomentTimezone } from '@/utils/dateUtils';

export default {
    name: 'MyTrip',
    title: 'Native American Tours',
    components: {
        NavBtns,
        FlowNavigation
    },
    data() {
        return {
            baseUrl: process.env.VUE_APP_BASE_URL,
            iframeStatus: true,
            tenantId: null,
            tourOperatorId: 0,
            comboIds: 0,
            packageId: 0,
            affiliateId: 0,
            cartItem: {},
            cartItemLength: 0,
            checkPackageIds: [],
            bookings: {},
            expandedDates: []
        };
    },
    computed: {
        tabs() {
            const hasCustomFields = this.$store.state.hasCustomFields;
            const isCombo = this.comboIds && this.comboIds !== 0;

            if (isCombo) {
                // Combo WITH add-ons: Browse(1), Init(2), Addons(3), MyTrip(4), Maps(5), Checkout(6)
                // Combo WITHOUT add-ons: Browse(1), Init(2), MyTrip(3), Maps(4), Checkout(5)
                return hasCustomFields === false ? 3 : 4;
            }

            // MyTrip is only for combo packages, shouldn't be here for single packages
            return 1;
        },
        showComboWarning() {
            return this.comboIds && this.cartItemLength < 2;
        }
    },
    async created() {
        this.initializeState();
        this.bookings = this.generateTimeSlots();

        // Expand all dates by default
        if (Object.keys(this.bookings).length > 0) {
            this.expandedDates = Object.keys(this.bookings);
        }

        this.$store.dispatch('storeTabs', this.tabs);
        this.$store.dispatch('storeMindChange', 0);
    },

    methods: {
        initializeState() {
            this.tenantId = this.$store.state.tenantId;
            this.tourOperatorId = this.$store.state.tourOperatorId;
            this.packageId = this.$store.state.packageId;
            this.affiliateId = this.$store.state.affiliateId;
            this.iframeStatus = this.$store.state.iframeStatus;
            this.comboIds = this.$store.state.comboIds;
            this.cartItem = this.$store.state.cartItem;
            this.cartItemLength = Object.values(this.cartItem).length;

            if (this.cartItemLength) {
                this.checkPackageIds = Object.values(this.cartItem).map(item => parseInt(item.package_id));
            }
        },
        toggleDate(date) {
            const index = this.expandedDates.indexOf(date);
            if (index > -1) {
                this.expandedDates.splice(index, 1);
            } else {
                this.expandedDates.push(date);
            }
        },
        editPackage(formData) {
            if (formData.affiliate_id) {
                this.$store.dispatch('storeAffiliateId', formData.affiliate_id);
            }
            this.$store.dispatch('storeFormData', formData);
            this.$store.dispatch('storePackageId', formData.package_id);
            this.$store.dispatch('storeDate', formData.date);
            this.$router.push({ name: 'Init' });
        },
        generateTimeSlots() {
            const bookingsByDate = {};

            // Get unique sorted dates
            const dates = [...new Set(
                Object.values(this.cartItem).map(item => item.date)
            )].sort();

            // Initialize bookings for each date
            dates.forEach(date => {
                bookingsByDate[date] = [];
            });

            // Process cart items
            Object.values(this.cartItem).forEach(item => {
                const currentDate = item.date;
                const slotTime = item.slot_time;

                bookingsByDate[currentDate].push({
                    time: slotTime,
                    slot_time: this.formatTime(slotTime),
                    booking: true,
                    data: item,
                });
            });

            // Sort bookings by time for each date
            Object.keys(bookingsByDate).forEach(date => {
                bookingsByDate[date].sort((a, b) => a.time.localeCompare(b.time));
            });

            return bookingsByDate;
        },
        formatDate(dateString, isClass = false) {
            if (isClass) {
                return getMomentTimezone(this.$store.state.timezone, dateString).format('YYYY-MM-DD');
            }

            return getMomentTimezone(this.$store.state.timezone, dateString).format('dddd, MMMM D, YYYY');
        },
        formatTime(timeString) {
            const [hours, minutes] = timeString.split(':').map(Number);
            const period = hours >= 12 ? 'PM' : 'AM';
            const formattedHours = hours % 12 || 12;
            return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
        },
        navigateToTab(tab, destination) {
            if ([1, 2, 3, 4, 5, 6].includes(tab)) {
                if (tab === 2) {
                    this.$store.dispatch('storeFormData', null);
                }
                this.$store.dispatch('storeMindChange', 1);
                this.$router.push({ name: destination });
            }
        },
        continueToMaps() {
            this.$router.push({ name: 'Maps' });
        },
        processLoader(loader) {
            loader.hide();
        },
    },
};
</script>